import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, X, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';

import useAdminCategories from '../../hooks/useAdminCategories';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/Toast';
import { parseApiError } from '../../utils/apiError';
import { buildCategoryIndex } from '../../utils/categoryTree';

export default function AdminCategories() {
  const navigate = useNavigate();
  const [showDeleted, setShowDeleted] = useState(false);
  const { categories, loading, update, remove, restore, hardDelete } = useAdminCategories({ includeDeleted: showDeleted });
  const { byId, depthOf, pathOf, childrenOf } = useMemo(() => buildCategoryIndex(categories), [categories]);
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [hardDeletingId, setHardDeletingId] = useState(null);

  const onSearch = (e) => {
    e.preventDefault();
    setQ(query.trim());
  };

  const onClearSearch = () => {
    setQuery('');
    setQ('');
  };

  const filtered = useMemo(() => {
    if (!q) return categories;
    const needle = q.toLowerCase();
    return categories.filter((c) => c.name?.toLowerCase().includes(needle));
  }, [categories, q]);

  const [sortField, setSortField] = useState(null); // null | 'name' | 'displayOrder' | 'depositPercent' | 'localPickupOnly' | 'active'
  const [sortDir, setSortDir] = useState('asc');

  const onSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      // third click on the same column clears the sort (back to server order)
      setSortField(null);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) =>
    sortField === field ? (
      sortDir === 'asc' ? <ArrowUp size={14} aria-hidden="true" /> : <ArrowDown size={14} aria-hidden="true" />
    ) : null;

  // Depth-first walk (parent, then its children, recursively) so the
  // indentation added below actually reflects adjacency — the server's flat
  // displayOrder/name order interleaves unrelated categories whenever two
  // parents' children happen to sort next to each other, which read as
  // (wrongly) nested under the wrong parent once indented.
  const treeOrdered = useMemo(() => {
    const filteredIds = new Set(filtered.map((c) => c._id));
    const result = [];
    const walk = (id) => {
      const cat = byId.get(id);
      if (cat && filteredIds.has(id)) result.push(cat);
      (childrenOf.get(id) || []).forEach((child) => walk(child._id));
    };
    (childrenOf.get('root') || []).forEach((c) => walk(c._id));
    return result;
  }, [filtered, byId, childrenOf]);

  // Status shows 3 states (Active/Inactive/Deleted) but `active` is only a
  // boolean — sorting on the raw field scattered deleted rows in with
  // whichever of Active/Inactive happened to share their `active` value.
  const statusRank = (c) => (c.isDeleted ? 2 : c.active ? 0 : 1);

  const sorted = useMemo(() => {
    if (!sortField) return treeOrdered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av = sortField === 'active' ? statusRank(a) : a[sortField];
      let bv = sortField === 'active' ? statusRank(b) : b[sortField];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, treeOrdered, sortField, sortDir]);

  // indentation only means something in tree order — an explicit column sort
  // breaks adjacency, so drop it rather than imply a hierarchy that isn't there
  const showIndent = !sortField;

  const onDelete = async (cat) => {
    const ok = await confirm({
      title: 'Delete category',
      message: `Delete "${cat.name}"? It will be hidden from the storefront.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove(cat._id);
      addToast('Category deleted', { variant: 'success' });
    } catch (err) {
      // Server returns 409 with { category: 'cannot delete: N product(s)...' }.
      const { formError, fieldErrors } = parseApiError(err);
      addToast(fieldErrors.category || formError || 'Could not delete category', { variant: 'error' });
    }
  };

  const toggleActive = async (cat) => {
    try {
      await update(cat._id, { active: !cat.active });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not update category', { variant: 'error' });
    }
  };

  const onRestore = async (cat) => {
    setRestoringId(cat._id);
    try {
      await restore(cat._id);
      addToast(`"${cat.name}" restored`, { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not restore category', { variant: 'error' });
    } finally {
      setRestoringId(null);
    }
  };

  const onHardDelete = async (cat) => {
    const ok = await confirm({
      title: 'Permanently Delete',
      message: `Permanently delete "${cat.name}"? This cannot be undone, and any category still linked to it as a parent will be moved to the top level.`,
      confirmLabel: 'Permanently delete',
      danger: true,
      confirmText: cat.name,
    });
    if (!ok) return;
    setHardDeletingId(cat._id);
    try {
      await hardDelete(cat._id);
      addToast(`"${cat.name}" permanently deleted`, { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not permanently delete category', { variant: 'error' });
    } finally {
      setHardDeletingId(null);
    }
  };

  return (
    <div>
      <div className="ss-admin__header">
        <h1>Categories</h1>
        <Button size="sm" onClick={() => navigate('/admin/categories/new')}>
          <Plus size={16} aria-hidden="true" /> New category
        </Button>
      </div>

      <div className="ss-admin__header" style={{ marginTop: 'var(--ss-space-4)' }}>
        <form className="ss-admin-search" onSubmit={onSearch}>
          <div className="ss-admin-search__field">
            <input
              className="ss-input"
              type="search"
              placeholder="Search categories…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search categories"
            />
            {query && (
              <button
                type="button"
                className="ss-admin-search__clear"
                onClick={onClearSearch}
                aria-label="Clear search"
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </div>
          <Button type="submit" variant="ghost" size="sm"><Search size={16} aria-hidden="true" /> Search</Button>
        </form>
        <Button
          type="button"
          variant={showDeleted ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setShowDeleted((v) => !v)}
        >
          {showDeleted ? 'Hide deleted' : 'Show deleted'}
        </Button>
      </div>

      {loading ? (
        <div className="ss-state"><Spinner label="Loading categories" size={32} /></div>
      ) : filtered.length === 0 ? (
        <div className="ss-state"><h2>{q ? 'No categories match your search' : 'No categories yet'}</h2></div>
      ) : (
        <>
          <Card className="ss-admin-table-wrap" style={{ marginTop: 'var(--ss-space-5)', padding: 0 }}>
            <div className="ss-table-scroll">
            <table className="ss-admin-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('name')}>
                      Name {sortIcon('name')}
                    </button>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('displayOrder')}>
                      Order {sortIcon('displayOrder')}
                    </button>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('depositPercent')}>
                      Deposit {sortIcon('depositPercent')}
                    </button>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('localPickupOnly')}>
                      Pickup only {sortIcon('localPickupOnly')}
                    </button>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button type="button" className="ss-admin-table__sort" onClick={() => onSort('active')}>
                      Status {sortIcon('active')}
                    </button>
                  </th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <span
                        style={showIndent ? { paddingLeft: `${depthOf.get(c._id) * 18}px` } : undefined}
                        title={pathOf.get(c._id)?.join(' › ')}
                      >
                        {showIndent && depthOf.get(c._id) > 0 && '— '}
                        {c.name}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{c.displayOrder}</td>
                    <td style={{ textAlign: 'center' }}>{c.depositPercent > 0 ? `${c.depositPercent}%` : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{c.localPickupOnly ? 'Yes' : '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {c.isDeleted ? (
                        <Badge>Deleted</Badge>
                      ) : (
                        <button
                          type="button"
                          className="ss-admin-toggle"
                          onClick={() => toggleActive(c)}
                          title={c.active ? 'Click to deactivate' : 'Click to activate'}
                        >
                          <Badge className={c.active ? 'ss-badge--active' : ''}>{c.active ? 'Active' : 'Inactive'}</Badge>
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="ss-admin-actions">
                        {c.isDeleted ? (
                          <>
                            <button
                              type="button"
                              className="ss-icon-btn"
                              onClick={() => onRestore(c)}
                              disabled={restoringId === c._id || hardDeletingId === c._id}
                              aria-label={`Restore ${c.name}`}
                            >
                              <RotateCcw size={16} aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="ss-icon-btn ss-icon-btn--danger-solid"
                              onClick={() => onHardDelete(c)}
                              disabled={restoringId === c._id || hardDeletingId === c._id}
                              aria-label={`Permanently delete ${c.name}`}
                            >
                              <Trash2 size={16} aria-hidden="true" />
                            </button>
                            {(restoringId === c._id || hardDeletingId === c._id) && (
                              <Spinner label={restoringId === c._id ? 'Restoring' : 'Deleting'} />
                            )}
                          </>
                        ) : (
                          <>
                            <button type="button" className="ss-icon-btn" onClick={() => navigate(`/admin/categories/${c._id}`)} aria-label={`Edit ${c.name}`}>
                              <Pencil size={16} aria-hidden="true" />
                            </button>
                            <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(c)} aria-label={`Delete ${c.name}`}>
                              <Trash2 size={16} aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>

          <div className="ss-admin-cards" style={{ marginTop: 'var(--ss-space-5)' }}>
            {sorted.map((c) => (
              <div className="ss-admin-card" key={c._id}>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__title" title={pathOf.get(c._id)?.join(' › ')}>
                    {showIndent && depthOf.get(c._id) > 0 && '— '}
                    {c.name}
                  </span>
                  {c.isDeleted ? (
                    <Badge>Deleted</Badge>
                  ) : (
                    <button
                      type="button"
                      className="ss-admin-toggle"
                      onClick={() => toggleActive(c)}
                      title={c.active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <Badge className={c.active ? 'ss-badge--active' : ''}>{c.active ? 'Active' : 'Inactive'}</Badge>
                    </button>
                  )}
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Order</span>
                  <span>{c.displayOrder}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Deposit</span>
                  <span>{c.depositPercent > 0 ? `${c.depositPercent}%` : '—'}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Pickup only</span>
                  <span>{c.localPickupOnly ? 'Yes' : '—'}</span>
                </div>
                <div className="ss-admin-card__row">
                  <div className="ss-admin-actions">
                    {c.isDeleted ? (
                      <>
                        <button
                          type="button"
                          className="ss-icon-btn"
                          onClick={() => onRestore(c)}
                          disabled={restoringId === c._id || hardDeletingId === c._id}
                          aria-label={`Restore ${c.name}`}
                        >
                          <RotateCcw size={16} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="ss-icon-btn ss-icon-btn--danger-solid"
                          onClick={() => onHardDelete(c)}
                          disabled={restoringId === c._id || hardDeletingId === c._id}
                          aria-label={`Permanently delete ${c.name}`}
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                        {(restoringId === c._id || hardDeletingId === c._id) && (
                          <Spinner label={restoringId === c._id ? 'Restoring' : 'Deleting'} />
                        )}
                      </>
                    ) : (
                      <>
                        <button type="button" className="ss-icon-btn" onClick={() => navigate(`/admin/categories/${c._id}`)} aria-label={`Edit ${c.name}`}>
                          <Pencil size={16} aria-hidden="true" />
                        </button>
                        <button type="button" className="ss-icon-btn ss-icon-btn--danger" onClick={() => onDelete(c)} aria-label={`Delete ${c.name}`}>
                          <Trash2 size={16} aria-hidden="true" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {dialog}
    </div>
  );
}
